import { useCallback, useEffect, useRef, useState } from 'react';
import { useVideoRecommendationChannel } from '@/context/channels/agent/video-recommendation';
import {
  useTriggerVideoRecommendation,
  type VideoRecommendationCard,
} from '@/data/agent/video-recommendation';

export enum VideoRecommendationStatus {
  Idle = 'idle',
  Loading = 'loading',
  SuccessVideo = 'success_video',
  SuccessText = 'success_text',
  Error = 'error',
}

// Safety net: if no event arrives within this window (e.g. a dropped broadcast or a job that
// never emits), the UI leaves the loading state instead of hanging forever.
const RESULT_TIMEOUT_MS = 45_000;

export const useVideoRecommendation = () => {
  const [status, setStatus] = useState<VideoRecommendationStatus>(
    VideoRecommendationStatus.Idle
  );
  const [video, setVideo] = useState<VideoRecommendationCard | null>(null);
  const [textMessage, setTextMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const seenVideoUrlsRef = useRef<string[]>([]);
  const sessionIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { mutate: trigger } = useTriggerVideoRecommendation();

  const clearResultTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearResultTimeout(), [clearResultTimeout]);

  const { subscribe } = useVideoRecommendationChannel({
    onRecommendation: (recommendedVideo) => {
      clearResultTimeout();
      seenVideoUrlsRef.current = [
        ...seenVideoUrlsRef.current,
        recommendedVideo.video_url,
      ];
      setVideo(recommendedVideo);
      setStatus(VideoRecommendationStatus.SuccessVideo);
    },
    onTextMessage: (message) => {
      clearResultTimeout();
      setTextMessage(message);
      setStatus(VideoRecommendationStatus.SuccessText);
    },
    onError: (message) => {
      clearResultTimeout();
      setErrorMessage(message);
      setStatus(VideoRecommendationStatus.Error);
    },
    onConnected: () => {
      if (!sessionIdRef.current) return;

      trigger({
        session_id: sessionIdRef.current,
        exclude_video_urls: seenVideoUrlsRef.current,
      });
    },
  });

  const generate = useCallback(() => {
    setStatus(VideoRecommendationStatus.Loading);
    setVideo(null);
    setTextMessage('');
    setErrorMessage('');

    const sessionId = `agent_video_${Date.now()}`;
    sessionIdRef.current = sessionId;
    subscribe(sessionId);

    clearResultTimeout();
    timeoutRef.current = setTimeout(() => {
      setErrorMessage(
        'A recomendação está demorando mais que o esperado. Tente novamente.'
      );
      setStatus(VideoRecommendationStatus.Error);
    }, RESULT_TIMEOUT_MS);
  }, [subscribe, clearResultTimeout]);

  return {
    status,
    video,
    textMessage,
    errorMessage,
    generate,
    regenerate: generate,
  };
};
